import { FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'
import styled from 'styled-components'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, CheckBox, SupportMessage, Text, TextInput } from '@mrshmllw/smores-react'

import { schema } from './schema/schema'
import { TrustpilotReviewBox } from '../../components/TrustpilotReviewBox'
import SplitPage, { SPLIT_PAGE_TABLET_BREAKPOINT } from 'directFlow/components/SplitPage/SplitPage'
import { State, usePersistentState } from 'directFlow/state'
import { Page } from 'directFlow/types'
import { EmailInputContainer } from 'components/EmailInputContainer'

import { useDirectFlowPath } from 'src/router/direct-flow'
import { identify, useBreakpoint, useTracking } from 'utils'

export const EmailCapture: FC = () => {
  const navigate = useNavigate()
  const path = useDirectFlowPath()
  const track = useTracking('Email Capture')
  const { quoteId } = useParams()

  if (!quoteId) {
    throw new Error('Route does not have a quote ID')
  }

  const deviceType = useBreakpoint(SPLIT_PAGE_TABLET_BREAKPOINT)
  const isMobile = deviceType === 'mobile'
  const persistentState = usePersistentState()
  const emailSubscribe = persistentState.getPageState(Page.DRIVERS).emailSubscribe

  const { handleSubmit, control, getValues } = useForm({
    defaultValues: {
      email: persistentState.getPageState(Page.DRIVERS).email || '',
      optedInToProductUpdates: emailSubscribe === undefined ? true : emailSubscribe,
      optedInToNbNurture: false,
    },
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  })

  const onValidSubmit = () => {
    const driversPageState = persistentState.getPageState(Page.DRIVERS)
    const { email, optedInToNbNurture, optedInToProductUpdates } = getValues()

    driversPageState.email = email
    driversPageState.emailSubscribe = optedInToProductUpdates
    persistentState.updatePageFields(Page.DRIVERS, driversPageState as State[Page.DRIVERS])

    identify(quoteId, {
      email: email.trim(),
      optedInToProductUpdates,
      optedInToNbNurture,
    })

    track('clickNextPage')

    return navigate(path(Page.REGISTRATION))
  }

  return (
    <SplitPage
      asideContent={
        <TrustpilotReviewBox defaultReview="Moved to the UK from somewhere else? We’ll count your driving experience from the last country you lived in as well, and get you an even better price!" />
      }
    >
      <form
        id="direct-form"
        noValidate
        onSubmit={handleSubmit(onValidSubmit, (errors) => {
          track(`clicksNextPageWithErrors - Email Capture`, { errors })
        })}
      >
        <Text tag="h1" typo="hero-alternate" mt={isMobile ? '64px' : undefined} mb="16px">
          Just in case you need to come back later...
        </Text>
        <Controller
          name="email"
          control={control}
          render={({ field: { ref, onChange, onBlur, value }, fieldState: { error } }) => (
            <EmailInputContainer value={value}>
              {(completed) => (
                <StyledInput
                  id="emailAddress"
                  ref={ref}
                  mt={isMobile ? '64px' : '48px'}
                  label="Your email"
                  renderAsTitle
                  placeholder="Enter details here..."
                  onChange={onChange}
                  value={value ? value.trim() : value}
                  type="email"
                  onBlur={() => {
                    onBlur()
                    track('email', { value: value.trim() })
                    identify(quoteId, {
                      email: value.trim(),
                    })
                  }}
                  onAutoFill={() => {
                    track('email', { value: value.trim() })
                    identify(quoteId, {
                      email: value.trim(),
                    })
                  }}
                  error={!!error}
                  errorMsg={error?.message}
                  completed={completed}
                />
              )}
            </EmailInputContainer>
          )}
        />
        <SupportMessage
          mt="48px"
          description={
            <>
              We&apos;ll save your progress and email it to you. That way you can come back later
              without having to start all over again. <br />
              <br /> We won&apos;t share your email address with anyone else.
            </>
          }
          type="info"
        />
        <Controller
          name="optedInToProductUpdates"
          control={control}
          render={({ field: { ref, onChange, value } }) => (
            <Box flex mt="48px">
              <CheckBox
                ref={ref}
                checked={value ?? false}
                toggle={() => {
                  const optedInToProductUpdates = !value
                  track('optedInToProductUpdates', { value: optedInToProductUpdates })
                  onChange(optedInToProductUpdates)
                }}
              >
                <CenterAlignedCheckboxText>
                  Send me information and relevant updates about my quote (optional)
                </CenterAlignedCheckboxText>
              </CheckBox>
            </Box>
          )}
        />
        <Controller
          name="optedInToNbNurture"
          control={control}
          render={({ field: { ref, onChange, value } }) => (
            <Box flex mt="8px">
              <CheckBox
                ref={ref}
                checked={value ?? false}
                toggle={() => {
                  const optedInToNbNurture = !value
                  track('optedInToNbNurture', { value: optedInToNbNurture })
                  onChange(optedInToNbNurture)
                }}
              >
                <CenterAlignedCheckboxText>
                  Be the first to know about our latest offers, competitions and new launches. You
                  can opt-out at any time (optional)
                </CenterAlignedCheckboxText>
              </CheckBox>
            </Box>
          )}
        />
        <Button primary mt="24px" forcedWidth="108px">
          Next
        </Button>
      </form>
    </SplitPage>
  )
}

const CenterAlignedCheckboxText = styled(Text)`
  font-size: 12px;
  line-height: inherit;
`

const StyledInput = styled(TextInput)`
  @media (min-width: ${SPLIT_PAGE_TABLET_BREAKPOINT}px) {
    max-width: 416px;
  }
`
